import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { apiRequest } from "@/lib/queryClient";
import { cashScopeKey } from "./use-branch-cash";
import { invalidateSupplierReceiptCashCaches } from "@/lib/supplier-payment-cache";

export type CrossBranchItem = {
  cashMovementId: string; workspaceId: string; forBranchId: string; forBranchName: string;
  paidFromBranchId: string; paidFromBranchName: string; cashBoxId: string; reference: string;
  amount: string; signedAmount?: string; status: string; revision: number; createdAt: string; updatedAt: string;
  decidedByUserId?: string | null; decidedByName?: string | null; decidedAt?: string | null; decisionReason?: string | null;
  canDecide: boolean; canResolve: boolean; isReversed: boolean; reviewed?: boolean;
  history: { action?: string; status?: string; actorName?: string; userName?: string; decidedByName?: string; createdAt?: string; at?: string; reason?: string; forBranchName?: string; details?: { reason?: string; forBranchId?: string; status?: string; previous?: { forBranchId?: string; status?: string } } }[];
};
export type CrossBranchFilters = { movementId?: string; jobId?: string; receiptId?: string; from?: string; to?: string };
export type CrossBranchAction = "confirm" | "reject" | "keep" | "move" | "review";
export type CrossBranchDecision = { action: CrossBranchAction; reason?: string; forBranchId?: string; expectedRevision: number; idempotencyKey: string };
function useScope() {
  const { currentWorkspace, user, roles } = useAuth();
  return { workspace: currentWorkspace?.id, ready: !!currentWorkspace?.id && !!user?.id,
    scope: cashScopeKey(currentWorkspace?.id, user, roles.find(r => r.id === user?.roleId)?.permissions.map(p => p.id)) };
}
export function useCrossBranchChoices(enabled = true) {
  const { scope, ready } = useScope();
  return useQuery<{ branches: { id: string; name: string }[] }>({
    queryKey: ["branch-cash", "cross-branch-choices", ...scope], enabled: ready && enabled,
    queryFn: async () => (await apiRequest("GET", "/api/cash/cross-branch/branches")).json(),
  });
}
export function useCrossBranchCash(branches?: string[], filters: CrossBranchFilters = {}, enabled = true) {
  const { scope, ready } = useScope();
  const ids = branches?.slice().sort().join(",");
  return useQuery<{ items: CrossBranchItem[] }>({
    queryKey: ["branch-cash", "cross-branch", ...scope, ids, filters],
    enabled: ready && enabled && (branches === undefined || branches.length > 0),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (ids !== undefined) params.set("branches", ids);
      Object.entries(filters).forEach(([key, value]) => { if (value) params.set(key, value); });
      return (await apiRequest("GET", `/api/cash/cross-branch?${params}`)).json();
    },
  });
}
export function useCrossBranchDecision() {
  const cache = useQueryClient(), { workspace } = useScope();
  return useMutation<unknown, Error, { id: string; body: CrossBranchDecision }>({
    mutationFn: async ({ id, body }) => (await apiRequest("POST", `/api/cash/cross-branch/${encodeURIComponent(id)}/decision`, body)).json(),
    onSuccess: async () => { await invalidateSupplierReceiptCashCaches(cache, workspace || ""); await cache.invalidateQueries({queryKey:["cash-where"]}); },
  });
}